import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark } from 'lucide-react';
import { useScraps } from '../context/ScrapContext';
import { useAuth } from '../context/AuthContext';
import { getScrappedCampaigns, scrapCampaign, unscrapCampaign } from '../api/campaigns';
import CampaignCard from '../components/CampaignCard';
import { CampaignCardSkeletonGrid } from '../components/CampaignCardSkeleton';
import styles from './ScrapPage.module.css';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import CampaignGrid from '../components/CampaignGrid';

export default function ScrapPage() {
  useDocumentMeta({ title: '저장', description: '저장한 체험단 목록', noIndex: true });
  const { isLoggedIn } = useAuth();
  const { isScrapped, scrap, unscrap } = useScraps();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn) { setLoading(false); return; }
    getScrappedCampaigns()
      .then((data) => {
        const items = data?.campaigns?.content ?? data?.content ?? (Array.isArray(data) ? data : []);
        setCampaigns(items);
        items.forEach((c) => scrap(c));
      })
      .catch(() => setCampaigns([]))
      .finally(() => setLoading(false));
  }, [isLoggedIn]);

  const handleBookmark = async (campaignId) => {
    const campaign = campaigns.find((c) => c.id === campaignId);
    if (!campaign) return;
    const alreadyScrapped = isScrapped(campaignId);
    if (alreadyScrapped) {
      const index = campaigns.findIndex((c) => c.id === campaignId);
      unscrap(campaignId);
      setCampaigns((prev) => prev.filter((c) => c.id !== campaignId));
      try { await unscrapCampaign(campaignId); }
      catch {
        scrap(campaign);
        // 되돌릴 때는 원래 있던 자리로 — 뒤에 붙이면 목록이 재정렬된 것처럼 보인다
        setCampaigns((prev) => [...prev.slice(0, index), campaign, ...prev.slice(index)]);
      }
    } else {
      scrap(campaign);
      try { await scrapCampaign(campaignId); }
      catch { unscrap(campaignId); }
    }
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>저장</h1>

      {!isLoggedIn ? (
        <div className={styles.empty}>
          <div className={styles.emptyIconCircle}>
            <Bookmark size={34} strokeWidth={1.8} />
          </div>
          <p className={styles.emptyTitle}>로그인이 필요해요</p>
          <p className={styles.emptySub}>로그인하면 저장한 체험단을 볼 수 있어요</p>
          <Link className={styles.emptyBtn} to="/login" state={{ from: '/scrap' }}>로그인</Link>
        </div>
      ) : loading ? (
        <CampaignGrid><CampaignCardSkeletonGrid count={8} /></CampaignGrid>
      ) : campaigns.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIconCircle}>
            <Bookmark size={34} strokeWidth={1.8} />
          </div>
          <p className={styles.emptyTitle}>저장한 체험단이 없어요</p>
          <Link className={styles.emptyBtn} to="/">체험단 보러가기</Link>
        </div>
      ) : (
        <CampaignGrid>
          {campaigns.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              onBookmark={handleBookmark}
              isScrapped={isScrapped(c.id)}
            />
          ))}
        </CampaignGrid>
      )}
    </div>
  );
}
